/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { Interpolation } from "@emotion/serialize";
import styled, { StyledComponent } from "@emotion/styled";
import { RequiredThemeKeys } from "@smartsheet/sds-theme-internal";
import { forwardRef, ReactNode, Ref, AllHTMLAttributes, DetailedHTMLProps, HTMLAttributes, useMemo } from "react";
import { useTheme } from "../../../theme/utils/useTheme";
import { typeRampDefinitions } from "../constants/deprecatedTypeRampConstants";

/**
 * @deprecated This component is being removed in V6+, February 2025, in favor of TypeRampV2.
 */
export interface TypeRampProps extends AllHTMLAttributes<HTMLElement> {
    /**
     * The list of children to render inline.
     */
    children: ReactNode;

    /**
     * String representation of the desired component.
     */
    variant: keyof typeof typeRampDefinitions;

    /**
     * A `clientId` which is a unique string that appears as a data attribute `data-client-id`
     * in the rendered code, serving as a hook for automated tests. Will be applied to the root
     * element of this component.
     */
    clientId?: string;

    /**
     * What element to render the wrapper as.
     * @default span
     */
    renderAs?: keyof JSX.IntrinsicElements;
}

const ComponentBase = styled("div")``;

/**
 * @deprecated This component is being removed in V6+, February 2025, in favor of TypeRampV2.
 */
export const TypeRamp = forwardRef(
    ({ renderAs, children, clientId, variant, ...rest }: TypeRampProps, ref: Ref<HTMLElement>): JSX.Element => {
        const { key } = useTheme();
        const themeMode = key as RequiredThemeKeys;

        const { component, theme, ...restStyles } = typeRampDefinitions[variant];
        // We need to perform this little remapping for a couple reasons:
        // 1. The TypeRamp component only supports lodestar-light (light) and oldestar right now.
        // 2. The typeRampDefinitions are part of the public API and cannot be changed to use the
        //    more centralized functions from src/util/theme.
        const fallbackThemeMode = (() => {
            switch (themeMode) {
                case "ursa-light":
                case "ursa-dark":
                case "lodestar-light":
                    return "light" as const;
                case "oldestar":
                    return "oldestar" as const;
            }
        })();
        const styleObject: Interpolation<undefined> = {
            ...restStyles,
            ...theme[fallbackThemeMode],
        };

        const element = renderAs ? renderAs : component;

        // Memoizing this to ensure we won't create a new component each time.
        // withComponent returns a new component.
        // do we need the theme in here too??
        const ComponentRoot = useMemo(() => {
            return ComponentBase.withComponent(element);
        }, [element]) as StyledComponent<
            DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement>,
            // This type doesn't matter here.
            // eslint-disable-next-line @typescript-eslint/ban-types
            object,
            Record<string, unknown>
        >;

        return (
            <ComponentRoot ref={ref} data-client-id={clientId} css={styleObject} {...rest}>
                {children}
            </ComponentRoot>
        );
    }
);

TypeRamp.displayName = "TypeRamp";
